import React from 'react';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import {
  WebRounded, InsightsRounded, AutoAwesomeRounded, CheckCircleRounded, FormatQuoteRounded,
  ArrowForwardRounded, DownloadRounded, BrushRounded, CategoryRounded, DashboardRounded, InterestsRounded,
} from '@mui/icons-material';

const SERVICE_ICONS = [WebRounded, InsightsRounded, AutoAwesomeRounded];
const SKILL_ICONS = [BrushRounded, CategoryRounded, DashboardRounded, InterestsRounded];

// One-page portfolio for a freelancer: intro, services, approach, skills,
// why-hire-me, selected work, counters, testimonials and a closing call to
// action. Laid out after the Phlox "Freelancer" demo, with original content.
const FreelancerSite = ({ template, onAction, preview = false }) => {
  const { primary, accent, bg } = template.colors;
  const f = template.freelancer;
  const act = onAction || (() => {});
  const sid = (id) => (preview ? undefined : id);
  const action = (href) => (preview ? { component: 'span' } : href ? { href } : { onClick: act });

  const section = { px: { xs: 3, md: 8 }, py: { xs: 7, md: 10 }, scrollMarginTop: 60 };
  const inner = { maxWidth: 1120, mx: 'auto' };
  const eyebrow = { color: accent, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', fontSize: 13, mb: 1.5 };
  const h2 = { fontWeight: 800, fontSize: { xs: 30, md: 42 }, lineHeight: 1.15, letterSpacing: '-0.02em', mb: 2 };
  const solidBtn = { bgcolor: accent, color: '#fff', px: 3.5, py: 1.25, '&:hover': { bgcolor: accent, opacity: 0.9 } };
  const ghostBtn = { color: primary, border: `1.5px solid ${primary}26`, px: 3.5, py: 1.25 };
  const card = { bgcolor: '#fff', borderRadius: 4, p: 3.5, boxShadow: `0 10px 30px ${primary}0f` };
  const art = (i, height, label = '') => (
    <Box sx={{
      height, borderRadius: 4,
      background: `linear-gradient(${130 + i * 40}deg, ${accent}40 0%, ${primary}1f 100%)`,
      display: 'grid', placeItems: 'center', color: `${primary}66`, fontWeight: 800, fontSize: 22,
    }}>{label}</Box>
  );

  return (
    <Box sx={{ bgcolor: bg, color: primary, fontFamily: '"Poppins", "Inter", sans-serif' }}>
      {/* Navigation */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: 2.5, display: 'flex', alignItems: 'center', gap: 3, bgcolor: bg }}>
        <Typography sx={{ fontWeight: 800, fontSize: 20, flex: 1 }}>
          {f.firstName}<Box component="span" sx={{ color: accent }}>.</Box>
        </Typography>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3.5 }}>
          {[['Home', 'home'], ['Work', 'work'], ['Contact', 'contact']].map(([label, id]) => (
            <Typography key={id} {...(preview ? { component: 'span' } : { component: 'a', href: `#${id}` })}
              sx={{ color: 'inherit', textDecoration: 'none', fontSize: 14, fontWeight: 500, opacity: 0.75, '&:hover': { opacity: 1 } }}>
              {label}
            </Typography>
          ))}
        </Box>
        <Button {...action()} size="small" sx={{ bgcolor: primary, color: '#fff', px: 2.5, '&:hover': { bgcolor: primary, opacity: 0.9 } }}>
          Hire me
        </Button>
      </Box>

      {/* Intro */}
      <Box component="section" id={sid('home')} sx={{ ...section, pt: { xs: 6, md: 9 } }}>
        <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: { xs: 5, md: 8 }, alignItems: 'center' }}>
          <Box>
            <Typography sx={eyebrow}>Hello, I'm</Typography>
            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 42, md: 64 }, lineHeight: 1.05, letterSpacing: '-0.03em', mb: 1.5 }}>
              {f.firstName} {f.lastName}
            </Typography>
            <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 600, color: accent, mb: 2.5 }}>{f.role}</Typography>
            <Typography sx={{ fontSize: 17, opacity: 0.7, mb: 4, maxWidth: 500, lineHeight: 1.8 }}>{f.intro}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button {...action()} sx={solidBtn}>Contact me</Button>
              <Button {...action('#work')} sx={ghostBtn}>See my work</Button>
            </Box>
          </Box>
          {/* Tool badges in place of a portrait */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2.5, maxWidth: 380, mx: { xs: 'auto', md: 0 }, width: '100%' }}>
            {f.tools.map((t, i) => (
              <Box key={t} sx={{
                aspectRatio: '1', borderRadius: 5, display: 'grid', placeItems: 'center',
                fontWeight: 800, fontSize: { xs: 38, md: 52 }, letterSpacing: '-0.02em',
                color: i % 3 === 0 ? '#fff' : primary,
                bgcolor: i % 3 === 0 ? accent : '#fff',
                boxShadow: `0 14px 34px ${primary}14`,
                transform: i % 2 ? 'translateY(22px)' : 'none',
              }}>{t}</Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Services */}
      <Box component="section" sx={section}>
        <Box sx={inner}>
          <Typography sx={eyebrow}>Services</Typography>
          <Typography component="h2" sx={h2}>What I can do for you</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mt: 4 }}>
            {f.services.map((s, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <Box key={s.title} sx={card}>
                  <Box sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: `${accent}1f`, color: accent, display: 'grid', placeItems: 'center', mb: 2.5 }}>
                    <Icon />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 19, mb: 1 }}>{s.title}</Typography>
                  <Typography sx={{ opacity: 0.68, lineHeight: 1.75 }}>{s.text}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Approach */}
      <Box component="section" sx={{ ...section, bgcolor: '#fff' }}>
        <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 8 }, alignItems: 'center' }}>
          {art(0, 340)}
          <Box>
            <Typography sx={eyebrow}>My approach</Typography>
            <Typography component="h2" sx={h2}>Design that moves your business forward</Typography>
            <Typography sx={{ opacity: 0.7, lineHeight: 1.8, mb: 3 }}>{f.approach}</Typography>
            {f.approachPoints.map(p => (
              <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                <CheckCircleRounded sx={{ color: accent }} />
                <Typography sx={{ fontWeight: 600 }}>{p}</Typography>
              </Box>
            ))}
            <Button {...action('#about')} sx={{ ...ghostBtn, mt: 2 }}>About me</Button>
          </Box>
        </Box>
      </Box>

      {/* Skills */}
      <Box component="section" id={sid('about')} sx={section}>
        <Box sx={inner}>
          <Typography sx={eyebrow}>My skills</Typography>
          <Typography component="h2" sx={h2}>Things I'm good at</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3, mt: 4 }}>
            {f.skills.map((s, i) => {
              const Icon = SKILL_ICONS[i % SKILL_ICONS.length];
              return (
                <Box key={s.title} sx={{ ...card, display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 46, height: 46, flexShrink: 0, borderRadius: '50%', bgcolor: primary, color: '#fff', display: 'grid', placeItems: 'center' }}>
                    <Icon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 700 }}>{s.title}</Typography>
                      <Typography sx={{ fontWeight: 700, color: accent }}>{s.level}%</Typography>
                    </Box>
                    <Typography sx={{ opacity: 0.65, fontSize: 14.5, mb: 1.5 }}>{s.text}</Typography>
                    <LinearProgress variant="determinate" value={s.level} aria-label={`${s.title} ${s.level}%`}
                      sx={{ height: 6, borderRadius: 3, bgcolor: `${primary}12`, '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 3 } }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Why hire me */}
      <Box component="section" sx={{ ...section, bgcolor: primary, color: '#fff' }}>
        <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: { xs: 3, md: 8 }, alignItems: 'center' }}>
          <Box>
            <Typography sx={eyebrow}>Why me</Typography>
            <Typography component="h2" sx={{ ...h2, color: '#fff' }}>Why hire me for your next project?</Typography>
            <Typography sx={{ opacity: 0.75, lineHeight: 1.8 }}>{f.why}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
            <Button {...action()} startIcon={<DownloadRounded />} sx={solidBtn}>Download CV</Button>
            <Button {...action()} sx={{ ...ghostBtn, color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>Book a call</Button>
          </Box>
        </Box>
      </Box>

      {/* Selected work */}
      <Box component="section" id={sid('work')} sx={section}>
        <Box sx={inner}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 5 }}>
            <Box>
              <Typography sx={eyebrow}>Portfolio</Typography>
              <Typography component="h2" sx={{ ...h2, mb: 0 }}>Selected work</Typography>
            </Box>
            <Button {...action()} endIcon={<ArrowForwardRounded />} sx={{ color: accent, fontWeight: 700 }}>View all</Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 7 } }}>
            {f.work.map((w, i) => (
              <Box key={w.title} sx={{
                display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: { xs: 3, md: 6 }, alignItems: 'center',
                '& > :first-of-type': { order: { md: i % 2 ? 2 : 1 } },
              }}>
                {art(i, 300, w.tag)}
                <Box sx={{ order: { md: i % 2 ? 1 : 2 } }}>
                  <Typography sx={{ ...eyebrow, mb: 1 }}>{w.tag}</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: 24, md: 30 }, mb: 1.5 }}>{w.title}</Typography>
                  <Typography sx={{ opacity: 0.7, lineHeight: 1.8, mb: 2.5 }}>{w.text}</Typography>
                  <Button {...action()} endIcon={<ArrowForwardRounded />} sx={{ color: primary, fontWeight: 700, px: 0 }}>Read case study</Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Counters */}
      <Box sx={{ px: { xs: 3, md: 8 }, pb: { xs: 7, md: 10 } }}>
        <Box sx={{ ...inner, ...card, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, textAlign: 'center', py: 4.5 }}>
          {f.stats.map(s => (
            <Box key={s.label}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 34, md: 44 }, color: accent, lineHeight: 1.1 }}>{s.value}</Typography>
              <Typography sx={{ opacity: 0.65, fontWeight: 500 }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Testimonials */}
      <Box component="section" sx={{ ...section, bgcolor: '#fff' }}>
        <Box sx={inner}>
          <Typography sx={{ ...eyebrow, textAlign: 'center' }}>Testimonials</Typography>
          <Typography component="h2" sx={{ ...h2, textAlign: 'center' }}>What clients say</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 4 }}>
            {f.testimonials.map(t => (
              <Box key={t.name} sx={{ bgcolor: bg, borderRadius: 4, p: 4 }}>
                <FormatQuoteRounded sx={{ color: accent, fontSize: 36 }} />
                <Typography sx={{ fontSize: 17, lineHeight: 1.8, opacity: 0.85, mb: 3 }}>{t.quote}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: accent, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700 }}>
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
                    <Typography sx={{ fontSize: 14, opacity: 0.6 }}>{t.title}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Call to action */}
      <Box component="section" id={sid('contact')} sx={section}>
        <Box sx={{
          ...inner, borderRadius: 6, p: { xs: 5, md: 8 }, textAlign: 'center', color: '#fff',
          background: `linear-gradient(135deg, ${primary} 0%, ${primary}e6 55%, ${accent} 150%)`,
        }}>
          <Typography component="h2" sx={{ ...h2, color: '#fff' }}>Let's work together</Typography>
          <Typography sx={{ opacity: 0.75, mb: 4, maxWidth: 520, mx: 'auto' }}>
            Have a product, brand or website in mind? Tell me about it and I'll reply within a day.
          </Typography>
          <Button {...action()} sx={solidBtn}>Contact me</Button>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 3, md: 8 }, py: 3, borderTop: `1px solid ${primary}12`, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 13, opacity: 0.6 }}>© {new Date().getFullYear()} {f.firstName} {f.lastName}. All rights reserved.</Typography>
      </Box>
    </Box>
  );
};

export default FreelancerSite;
